import { create } from 'zustand';

export const useConversationStore = create((set, get) => ({
  conversations: [],
  loading: false,
  error: null,
  selectedConversationId: null,

  // --- Derived selectors (computed from conversations) ---
  pendingIncoming: () => {
    return get().conversations.filter(
      (c) => c.conversation_type === 'pending_invite_incoming'
    );
  },

  pendingOutgoing: () => {
    return get().conversations.filter(
      (c) => c.conversation_type === 'pending_invite_outgoing'
    );
  },

  activeDMs: () => {
    return get().conversations.filter(
      (c) => c.conversation_type === 'active_matrix_dm'
    );
  },

  findDmByPeerUid: (peerUid) => {
    const p = Number(peerUid);
    if (!Number.isFinite(p)) return null;
    return get().conversations.find(
      (c) => c.conversation_type === 'active_matrix_dm' && Number(c.other_user_id) === p
    ) || null;
  },

  // --- Actions ---
  loadConversations: async () => {
    set({ loading: true, error: null });

    try {
      const res = await fetch('/api/conversations', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(
          '[conversationStore] /api/conversations HTTP error:',
          res.status,
          text
        );
        set({ loading: false, error: `HTTP ${res.status}` });
        return;
      }

      const data = await res.json().catch(() => null);

      // conversations.js responds with: { success: true, conversations: rows }
      // so we only care that data.conversations is an array.
      if (!data || !Array.isArray(data.conversations)) {
        console.error(
          '[conversationStore] Bad payload from /api/conversations:',
          data
        );
        set({ loading: false, error: 'Invalid response' });
        return;
      }

      set({
        conversations: data.conversations,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('[conversationStore] loadConversations failed:', err);
      set({
        loading: false,
        error: String(err.message || err),
      });
    }
  },

  // Local patch helper: update a single conversation row by dm_id
  updateConversationByDmId: (dmId, patch) => {
    const d = Number(dmId);
    if (!Number.isFinite(d) || !patch || typeof patch !== 'object') return;

    set((state) => {
      const list = state.conversations || [];
      const idx = list.findIndex((c) => Number(c.dm_id) === d);

      if (idx === -1) {
        return {};
      }

      const next = [...list];
      next[idx] = {
        ...next[idx],
        ...patch,
      };

      return { conversations: next };
    });
  },

  // Local patch helper: update conversation(s) for a given peer (other_user_id)
  updateConversationByPeerUid: (peerUid, patch) => {
    const p = Number(peerUid);
    if (!Number.isFinite(p) || !patch || typeof patch !== 'object') return;

    set((state) => {
      const list = state.conversations || [];
      let changed = false;

      const next = list.map((c) => {
        const otherId = Number(c.other_user_id || 0);
        if (!Number.isFinite(otherId) || otherId !== p) {
          return c;
        }
        changed = true;
        return {
          ...c,
          ...patch,
        };
      });

      if (!changed) {
        return {};
      }

      return { conversations: next };
    });
  },

  selectConversation: (conversationId) => {
    set({ selectedConversationId: conversationId });
  },

  clearSelection: () => {
    set({ selectedConversationId: null });
  },
}));


